import { useState, useEffect, useCallback, useMemo } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { deleteItem, getItems, getTransaction } from "../../services/services";
import { toast } from "react-toastify";
import ItemList from "./ItemList";
import StockTransactions from "./StockTransactions";
import { confirmDelete } from "../../components/confirmDelete";
import { useNavigate } from "react-router";
import SearchFilter from "../purchase/SearchFilter";
import { Link } from "react-router-dom";

const Items = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [minStockAlert, setMinStock] = useState("");
  const [expiryMonths, setExpiryMonths] = useState("");

  const navigate = useNavigate();

  const userId = JSON.parse(localStorage.getItem("Login_User"))?._id;

  const fetchItems = useCallback(async () => {
    if (!userId) {
      toast.error("User not logged in");
      return;
    }

    setIsLoadingItems(true);

    try {
      const params = {
        name: searchTerm,
        from: dateRange?.from,
        to: dateRange?.to,
        minStockAlert,
        expiryMonths,
        page,
        limit,
      };

      const { data } = await getItems(params);

      const products = data?.response?.products || [];

      setProducts(
        products.map((p) => ({
          ...p,
          id: p._id,
          batches: p.batches ?? [],
        })),
      );

      setTotalPages(data?.totalPages ?? 1);
      setTotalItems(data?.totalItems ?? 0);
    } catch (err) {
      console.error("Fetch Items Error:", err);
      toast.error(err?.response?.data?.error || "Failed to fetch items");

      setProducts([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoadingItems(false);
    }
  }, [userId, searchTerm, minStockAlert, expiryMonths, dateRange, page, limit]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (selectedProduct?.id && !isEditMode) {
      setShowSidebar(true);
    } else {
      setShowSidebar(false);
    }
  }, [selectedProduct?.id, isEditMode]);

  const handleDelete = async (id, name) => {
    const product = products.find((p) => p.id === id);
    if (!product) {
      toast.error("Product not found");
      return;
    }

    try {
      const response = await getTransaction({ productId: id });
      const ledgerEntries = response.data.ledgerEntries || [];

      const hasRestrictedTxn = ledgerEntries.some((entry) =>
        ["sale", "purchase", "saleReturn", "purchaseReturn"].includes(
          entry.referenceType,
        ),
      );
      const isConfirm = await confirmDelete(product.name);
      if (!isConfirm) return;

      await deleteItem(id);
      await fetchItems();

      if (selectedProduct?.id === id) {
        setSelectedProduct(null);
        setShowSidebar(false);
      }

      toast.success("Item deleted successfully");
    } catch (error) {
      console.error(
        "Error deleting item:",
        error.response?.data || error.message,
      );
      toast.error(error.response?.data?.error || "Failed to delete item");
    }
  };

  const handleSelectProduct = useCallback((product) => {
    const selected = {
      ...product,
      batch: {
        ...product.batch,
        quantity: product.batch.quantity?.toString() || "",
        remainingQty: product.batch.remainingQty?.toString() || "",
        purchasePrice: product.batch.purchasePrice?.toString() || "",
        taxType: product.batch.taxType || "exclusive",
        purchaseDate: product.batch.purchaseDate || new Date().toISOString(),
        expiryDate: product.batch.expiryDate || new Date().toISOString(),
        party: product.batch.party || "",
      },
    };
    setSelectedProduct(selected);
  }, []);

  const openItemModal = (editMode = false, product = null) => {
    if (editMode && product) {
      // Directly navigate with existing product data
      navigate("/items/add", {
        state: {
          product,
          isEditMode: true,
          isPurchase: false,
        },
      });
    } else {
      navigate("/items/add", {
        state: { isPurchase: false },
      });
    }
  };

  const handleCloseSidebar = () => {
    setShowSidebar(false);
    setSelectedProduct(null);
  };

  return (
    <div className="products-container" id="main">
      <div className="d-flex align-items-center justify-content-between mb-2 gap-3 flex-column flex-md-row">
        <div className="d-flex align-items-center justify-content-between w-100">
          <h4 className="custom-title mb-0 text-start">Manage Products</h4>

          {/* Mobile Button */}
          <Link to="/items/add" className="d-block d-md-none">
            <button className="btn btn-primary p-2 text-nowrap">
              Add Item
            </button>
          </Link>
        </div>

        {/* Search */}
        <SearchFilter
          showItemName={false}
          showProductFilters={true}
          onSearch={({ query }) => {
            setPage(1);

            // reset all first
            setSearchTerm("");
            setDateRange({ from: "", to: "" });
            setMinStock("");
            setExpiryMonths("");

            if (query?.fromDate) {
              setDateRange({
                from: query.fromDate,
                to: query.toDate,
              });
            } else if (query?.searchBy) {
              const parsed = JSON.parse(query.searchBy);
              setSearchTerm(parsed.name || "");
            } else if (query?.minStockAlert) {
              setMinStock(String(query.minStockAlert));
            } else if (query?.expiryMonths) {
              setExpiryMonths(String(query.expiryMonths));
            }
          }}
        />
        {/* Desktop Button */}
        <button
          className="btn btn-primary p-2 text-nowrap d-none d-md-block"
          onClick={() => navigate("/items/add")}
        >
          Add Item
        </button>
      </div>

      <div>
        <div>
          <div
            className="font-semibold text-gray-700 mb-2"
            style={{ fontSize: "14px", fontWeight: "600" }}
          >
            Total Products: {totalItems}
          </div>

          <ItemList
            products={products}
            isLoading={isLoadingItems}
            selectedProductId={selectedProduct?.id}
            onSelectProduct={handleSelectProduct}
            openItemModal={openItemModal}
            deleteItem={handleDelete}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            totalPages={totalPages}
            totalItems={totalItems}
          />
        </div>

        {showSidebar && selectedProduct && (
          <div className="overlay" onClick={handleCloseSidebar}>
            {/* <div className="bg-light" onClick={(e) => e.stopPropagation()}>
              <StockTransactions
                transactions={transactions}
                isLoading={isLoadingItems}
                selectedProduct={selectedProduct}
                selectedTransactionId={null}
                onSelectTransaction={setTransactions}
                onClose={handleCloseSidebar}
                onSuccess={handleSuccess}
              />
            </div> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default headerLayoutWrapper(Items);
